import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { Link } from '@tanstack/react-router'

import { Button } from '@gto/ui/button'
import { IconButton } from '@gto/ui/icon-button'
import { Paper } from '@gto/ui/paper'
import { Typography } from '@gto/ui/typography'

import {
  ArrowRightSmallIcon,
  CheckIcon,
  SendCircleIcon,
  SendIcon,
  SocialsIcon,
  UserPlusIcon,
  WhatsUpIcon,
} from '@/assets/icons'
import { useSupportLinks } from '@/components/support-links'
import type { Translations } from '@/localise'

type FreeBankrollActionSectionProps = {
  isAuthorized: boolean
}

const renderDescriptionLink = (href: string | undefined, chunks: ReactNode): ReactNode => {
  if (typeof href !== 'string' || href.length === 0) {
    return chunks
  }

  return (
    <a
      className="text-inherit underline"
      href={href}
      rel="noopener noreferrer"
      target="_blank"
    >
      {chunks}
    </a>
  )
}

export const FreeBankrollActionSection = ({ isAuthorized }: FreeBankrollActionSectionProps) => {
  const supportLinks = useSupportLinks()
  const discordLink = supportLinks.find((link) => link.gaTag === 'discord')
  const whatsappLink = supportLinks.find((link) => link.gaTag === 'whatsapp')
  const telegramLink = supportLinks.find((link) => link.gaTag === 'telegram')

  const messageLinks = [
    {
      id: 'discord',
      icon: (
        <SocialsIcon
          height={32}
          width={32}
        />
      ),
      link: discordLink?.href,
      ariaLabel: 'Discord RakeBack Wizard',
    },
    {
      id: 'whatsapp',
      icon: (
        <WhatsUpIcon
          height={32}
          width={32}
        />
      ),
      link: whatsappLink?.href,
      ariaLabel: 'WhatsApp RakeBack Wizard',
    },
    {
      id: 'telegram',
      icon: (
        <SendCircleIcon
          height={32}
          width={32}
        />
      ),
      link: telegramLink?.href,
      ariaLabel: 'Telegram RakeBack Wizard',
    },
  ].filter((item) => typeof item.link === 'string' && item.link.length > 0)

  return (
    <section className="flex flex-col py-[52px] md:py-[40px]">
      <div className="flex flex-col items-center gap-1 text-center">
        <Typography
          className="text-center align-middle"
          component="h2"
          variant="h4"
        >
          <FormattedMessage
            id="freeBankroll.action.title"
            values={{ date: 'May 10th, 2026' }}
          />
        </Typography>
        <Typography
          className="text-center"
          variant="button/large"
        >
          <FormattedMessage id="freeBankroll.action.subtitle" />
        </Typography>
      </div>

      <div className="mt-8 flex flex-col gap-6">
        <ActionRow
          descriptionId="freeBankroll.action.createAccount.description"
          titleId="freeBankroll.action.createAccount.title"
          icon={
            <UserPlusIcon
              height={32}
              width={32}
            />
          }
        >
          {isAuthorized ? (
            <div className="w-auto md:w-full [&>*]:w-auto md:[&>*]:w-full">
              <Button
                className="bg-[#313133] text-primary-main shadow-[0px_4px_24px_0px_#FFAB0057] hover:bg-[#313133]"
                size="large"
                variant="contained"
                endIcon={
                  <div className="inline-flex size-12">
                    <CheckIcon
                      height={24}
                      width={24}
                    />
                  </div>
                }
              >
                <FormattedMessage id="freeBankroll.steps.complete" />
              </Button>
            </div>
          ) : (
            <div className="w-auto md:w-full [&>*]:w-auto md:[&>*]:w-full">
              <Button
                as={Link}
                preload={false}
                size="large"
                to="/login"
                variant="contained"
                endIcon={
                  <div className="inline-flex size-12">
                    <ArrowRightSmallIcon
                      height={24}
                      width={24}
                    />
                  </div>
                }
              >
                <FormattedMessage id="freeBankroll.steps.createAccount" />
              </Button>
            </div>
          )}
        </ActionRow>

        <ActionRow
          descriptionId="freeBankroll.action.discord.description"
          titleId="freeBankroll.action.discord.title"
          icon={
            <SocialsIcon
              height={32}
              width={32}
            />
          }
        >
          <div className="w-auto md:w-full [&>*]:w-auto md:[&>*]:w-full">
            {discordLink ? (
              <Button
                as="a"
                href={discordLink.href}
                rel="noopener noreferrer"
                size="large"
                target="_blank"
                variant="contained"
              >
                <FormattedMessage id="freeBankroll.steps.discord" />
              </Button>
            ) : (
              <Button
                disabled
                size="large"
                variant="contained"
              >
                <FormattedMessage id="freeBankroll.steps.discord" />
              </Button>
            )}
          </div>
        </ActionRow>

        <ActionRow
          titleId="freeBankroll.action.message.title"
          description={
            <FormattedMessage
              id="freeBankroll.action.message.description"
              values={{
                discord: (chunks: ReactNode) => renderDescriptionLink(discordLink?.href, chunks),
                whatsapp: (chunks: ReactNode) => renderDescriptionLink(whatsappLink?.href, chunks),
                telegram: (chunks: ReactNode) => renderDescriptionLink(telegramLink?.href, chunks),
              }}
            />
          }
          icon={
            <SendIcon
              height={32}
              width={32}
            />
          }
        >
          <div className="flex gap-3">
            {messageLinks.map((item) => (
              <IconButton
                key={item.id}
                aria-label={item.ariaLabel}
                className="h-12 w-16 rounded-sm bg-paper-elevation4 text-white hover:bg-paper-elevation5"
                component="a"
                href={item.link}
                rel="noopener noreferrer"
                target="_blank"
              >
                <span className="inline-flex size-16">{item.icon}</span>
              </IconButton>
            ))}
          </div>
        </ActionRow>
      </div>
    </section>
  )
}

type ActionRowProps = {
  icon: ReactNode
  titleId: keyof Translations
  descriptionId?: keyof Translations
  description?: ReactNode
  children: ReactNode
}

const ActionRow = ({ icon, titleId, descriptionId, description, children }: ActionRowProps) => {
  return (
    <Paper
      className="rounded-md border border-divider px-6 py-5 md:px-4 md:py-6"
      elevation="1"
    >
      <div className="flex items-center justify-between gap-4 md:flex-col md:items-stretch md:gap-6">
        <div className="flex items-center gap-3 text-left md:flex-col md:text-center">
          <div className="inline-flex size-16 text-white">{icon}</div>
          <div>
            <Typography variant="button/large">
              <FormattedMessage id={titleId} />
            </Typography>
            <Typography variant="body2">
              {description ?? (descriptionId ? <FormattedMessage id={descriptionId} /> : null)}
            </Typography>
          </div>
        </div>
        <div className="flex w-auto justify-end md:w-full md:justify-center">{children}</div>
      </div>
    </Paper>
  )
}
